"use client";

// Client-island charts for the /stats page. The page itself stays a
// server component (all numbers render in the HTML for SEO); these handle only
// the visuals via Chart.js, the same library the Knowledge Demon dashboard
// uses, styled to match it: rounded bars, hidden legends, muted ticks.
import { c as _c } from "react/compiler-runtime";
import { Chart as ChartJS, BarElement, ArcElement, LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Filler, type ChartOptions, type TooltipItem, type Plugin, type TooltipModel } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import { useEffect, useRef, useState, type ReactNode } from "react";
ChartJS.register(BarElement, ArcElement, LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Filler);

// This page mounts ~17 Chart.js canvases. Building them all during hydration
// stalls the main thread even though every byte arrived fast (measured
// 2026-08-25: TTFB 0.19s, but the page still felt slow). Each chart now waits
// until it is near the viewport, so a first paint builds the two or three
// that are actually visible. The wrapper keeps the chart's footprint reserved
// so nothing shifts when it fills in.
function DeferredChart(t0) {
  const $ = _c(7);
  const {
    children,
    className,
    style
  } = t0;
  const ref = useRef(null);
  const [show, setShow] = useState(false);
  let t1;
  let t2;
  if ($[0] !== show) {
    t1 = () => {
      if (show) {
        return;
      }
      const el = ref.current;
      if (!el) {
        return;
      }
      if (typeof IntersectionObserver === "undefined") {
        setShow(true);
        return;
      }
      const io = new IntersectionObserver(entries => {
        if (entries.some(_temp)) {
          setShow(true);
          io.disconnect();
        }
      }, {
        rootMargin: "400px"
      });
      io.observe(el);
      return () => io.disconnect();
    };
    t2 = [show];
    $[0] = show;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  const t3 = show ? children : null;
  let t4;
  if ($[3] !== className || $[4] !== style || $[5] !== t3) {
    t4 = <div ref={ref} className={className} style={style}>{t3}</div>;
    $[3] = className;
    $[4] = style;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
}

// Theme hexes (canvas rendering needs resolved colors, not CSS vars).
function _temp(e) {
  return e.isIntersecting;
}
const GOLD = "#d4a843";
const TEXT_SECONDARY = "#a1a1aa";
const TEXT_MUTED = "#8a8a93";

// Per-option donut palette (matches the legend dots rendered on the page).
export const OPTION_HEX = ["#f59e0b", "#38bdf8", "#34d399", "#fb7185"];

// Shared dark tooltip, matching the site's card surfaces.
const TOOLTIP_BASE = {
  backgroundColor: "#15151a",
  borderColor: "#33333a",
  borderWidth: 1,
  cornerRadius: 6,
  padding: 8,
  titleColor: "#e5e5e5",
  bodyColor: TEXT_SECONDARY,
  displayColors: false,
  titleFont: {
    size: 12
  },
  bodyFont: {
    size: 12
  }
} as const;

// Longest y-axis label before it gets an ellipsis; the tooltip title always
// carries the full name, so nothing is lost on hover.
const MAX_LABEL = 24;

/** Resolve "var(--color-x)" through the live stylesheet so chart colors track
 *  the theme; plain hexes pass through. Empty when the var doesn't exist, so
 *  callers can fall back. Canvas drawing only happens client-side. */
function resolveColor(color: string): string {
  if (!color.startsWith("var(")) return color;
  if (typeof window === "undefined") return "";
  return getComputedStyle(document.documentElement).getPropertyValue(color.slice(4, -1)).trim();
}
interface Datum {
  name: string;
  value: number;
  /** Short value shown at the end of the bar (e.g. "55.2%", "12,345"). */
  display: string;
  /** Optional longer hover text (e.g. "55.2% win rate · 31% of runs"). */
  detail?: string;
  /** Optional per-bar color; takes a hex or a "var(--color-x)" reference. */
  color?: string;
}

/** Bar chart for ranked lists and win-rate breakdowns. Horizontal rows by
 * default; `vertical` flips to columns (used when several charts share one
 * row). Negative values grow downward, so delta charts read as dips. */
export function RankBars(t0) {
  const $ = _c(58);
  const {
    data,
    color: t1,
    vertical: t2
  } = t0;
  const color = t1 === undefined ? GOLD : t1;
  const vertical = t2 === undefined ? false : t2;
  const height = vertical ? 240 : Math.max(96, data.length * 32);
  let t3;
  if ($[0] !== data) {
    t3 = data.map(_temp2);
    $[0] = data;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const labels = t3;
  let t4;
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      display: false
    };
    t5 = {
      display: false
    };
    $[2] = t4;
    $[3] = t5;
  } else {
    t4 = $[2];
    t5 = $[3];
  }
  const t6 = vertical ? 10 : 12;
  let t7;
  if ($[4] !== t6) {
    t7 = {
      size: t6
    };
    $[4] = t6;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  const t8 = vertical ? 60 : 0;
  let t9;
  if ($[6] !== labels || $[7] !== t7 || $[8] !== t8) {
    t9 = {
      grid: t4,
      border: t5,
      ticks: {
        color: TEXT_SECONDARY,
        font: t7,
        autoSkip: false,
        maxRotation: t8,
        callback(value) {
          const label = labels[Number(value)] ?? "";
          return label.length > MAX_LABEL ? `${label.slice(0, MAX_LABEL - 1)}…` : label;
        }
      }
    };
    $[6] = labels;
    $[7] = t7;
    $[8] = t8;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  const catAxis = t9;
  const t10 = vertical ? "x" : "y";
  let t11;
  if ($[10] !== vertical) {
    t11 = vertical ? {
      padding: {
        top: 18
      }
    } : {
      padding: {
        right: 56
      }
    };
    $[10] = vertical;
    $[11] = t11;
  } else {
    t11 = $[11];
  }
  let t12;
  if ($[12] !== catAxis || $[13] !== vertical) {
    t12 = vertical ? {
      x: catAxis,
      y: {
        display: false,
        beginAtZero: true
      }
    } : {
      x: {
        display: false,
        beginAtZero: true
      },
      y: catAxis
    };
    $[12] = catAxis;
    $[13] = vertical;
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  let t13;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      display: false
    };
    $[15] = t13;
  } else {
    t13 = $[15];
  }
  let t14;
  if ($[16] !== labels) {
    t14 = items => labels[items[0]?.dataIndex ?? 0];
    $[16] = labels;
    $[17] = t14;
  } else {
    t14 = $[17];
  }
  let t15;
  if ($[18] !== data) {
    t15 = item => {
      const d_0 = data[item.dataIndex];
      return d_0?.detail ?? d_0?.display ?? "";
    };
    $[18] = data;
    $[19] = t15;
  } else {
    t15 = $[19];
  }
  let t16;
  if ($[20] !== t14 || $[21] !== t15) {
    t16 = {
      ...TOOLTIP_BASE,
      callbacks: {
        title: t14,
        label: t15
      }
    };
    $[20] = t14;
    $[21] = t15;
    $[22] = t16;
  } else {
    t16 = $[22];
  }
  const t17 = vertical ? 9 : 11;
  let t18;
  if ($[23] !== t17) {
    t18 = {
      size: t17
    };
    $[23] = t17;
    $[24] = t18;
  } else {
    t18 = $[24];
  }
  let t19;
  if ($[25] !== data) {
    t19 = (_value, ctx) => data[ctx.dataIndex]?.display ?? "";
    $[25] = data;
    $[26] = t19;
  } else {
    t19 = $[26];
  }
  let t20;
  if ($[27] !== t18 || $[28] !== t19) {
    t20 = {
      anchor: "end",
      align: "end",
      offset: 2,
      clamp: true,
      color: TEXT_MUTED,
      font: t18,
      formatter: t19
    };
    $[27] = t18;
    $[28] = t19;
    $[29] = t20;
  } else {
    t20 = $[29];
  }
  let t21;
  if ($[30] !== t16 || $[31] !== t20) {
    t21 = {
      legend: t13,
      tooltip: t16,
      datalabels: t20
    };
    $[30] = t16;
    $[31] = t20;
    $[32] = t21;
  } else {
    t21 = $[32];
  }
  let t22;
  if ($[33] !== t10 || $[34] !== t11 || $[35] !== t12 || $[36] !== t21) {
    t22 = {
      indexAxis: t10,
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      layout: t11,
      scales: t12,
      plugins: t21
    };
    $[33] = t10;
    $[34] = t11;
    $[35] = t12;
    $[36] = t21;
    $[37] = t22;
  } else {
    t22 = $[37];
  }
  const options = t22;
  let t23;
  if ($[38] !== height) {
    t23 = {
      height
    };
    $[38] = height;
    $[39] = t23;
  } else {
    t23 = $[39];
  }
  let t24;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = [ChartDataLabels as Plugin<"bar">];
    $[40] = t24;
  } else {
    t24 = $[40];
  }
  let t25;
  if ($[41] !== data) {
    t25 = data.map(_temp3);
    $[41] = data;
    $[42] = t25;
  } else {
    t25 = $[42];
  }
  let t26;
  if ($[43] !== color || $[44] !== data) {
    t26 = ctx_0 => resolveColor(data[ctx_0.dataIndex]?.color ?? color) || color;
    $[43] = color;
    $[44] = data;
    $[45] = t26;
  } else {
    t26 = $[45];
  }
  let t27;
  if ($[46] !== t25 || $[47] !== t26) {
    t27 = [{
      data: t25,
      backgroundColor: t26,
      borderRadius: 4,
      barPercentage: 0.85,
      categoryPercentage: 0.9
    }];
    $[46] = t25;
    $[47] = t26;
    $[48] = t27;
  } else {
    t27 = $[48];
  }
  let t28;
  if ($[49] !== labels || $[50] !== t27) {
    t28 = {
      labels,
      datasets: t27
    };
    $[49] = labels;
    $[50] = t27;
    $[51] = t28;
  } else {
    t28 = $[51];
  }
  let t29;
  if ($[52] !== options || $[53] !== t28) {
    t29 = <Bar plugins={t24} data={t28} options={options} />;
    $[52] = options;
    $[53] = t28;
    $[54] = t29;
  } else {
    t29 = $[54];
  }
  let t30;
  if ($[55] !== t23 || $[56] !== t29) {
    t30 = <DeferredChart style={t23}>{t29}</DeferredChart>;
    $[55] = t23;
    $[56] = t29;
    $[57] = t30;
  } else {
    t30 = $[57];
  }
  return t30;
}

// Shared HTML tooltip for the donuts. Chart.js draws native tooltips inside
// the canvas, and a 96px canvas would clip them, so hover renders one
// absolutely positioned element on <body> instead (one element total, reused
// by every donut on the page).
function _temp3(d_1) {
  return d_1.value;
}
function _temp2(d) {
  return d.name;
}
let donutTip: HTMLDivElement | null = null;
function donutTooltip(ctx: {
  chart: ChartJS;
  tooltip: TooltipModel<"doughnut">;
}) {
  const {
    chart,
    tooltip
  } = ctx;
  if (!donutTip) {
    donutTip = document.createElement("div");
    Object.assign(donutTip.style, {
      position: "absolute",
      pointerEvents: "none",
      background: "#15151a",
      border: "1px solid #33333a",
      borderRadius: "6px",
      padding: "4px 8px",
      fontSize: "12px",
      whiteSpace: "nowrap",
      zIndex: "50",
      transform: "translate(-50%, -130%)",
      opacity: "0"
    });
    document.body.appendChild(donutTip);
  }
  if (tooltip.opacity === 0) {
    donutTip.style.opacity = "0";
    return;
  }
  const item = tooltip.dataPoints?.[0];
  if (!item) return;
  donutTip.replaceChildren();
  const label = document.createElement("span");
  label.style.color = TEXT_SECONDARY;
  label.textContent = String(item.label ?? "");
  const value = document.createElement("span");
  Object.assign(value.style, {
    color: "#e5e5e5",
    fontWeight: "600",
    marginLeft: "6px"
  });
  value.textContent = `${item.parsed}%`;
  donutTip.append(label, value);
  const rect = chart.canvas.getBoundingClientRect();
  donutTip.style.left = `${rect.left + window.scrollX + tooltip.caretX}px`;
  donutTip.style.top = `${rect.top + window.scrollY + tooltip.caretY}px`;
  donutTip.style.opacity = "1";
}

/** Fixed-size donut for one event's option split. Non-responsive (a wall of
 *  these shouldn't spin up dozens of ResizeObservers); hovering a slice shows
 *  its option label and share via the shared HTML tooltip above. */
export function EventDonut(t0) {
  const $ = _c(27);
  const {
    options,
    size: t1,
    colors
  } = t0;
  const size = t1 === undefined ? 96 : t1;
  let t2;
  if ($[0] !== size) {
    t2 = {
      width: size,
      height: size
    };
    $[0] = size;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== options) {
    t3 = options.map(_temp4);
    $[2] = options;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== options) {
    t4 = options.map(_temp5);
    $[4] = options;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== colors || $[7] !== options) {
    let t6;
    if ($[9] !== colors) {
      t6 = (_, i) => colors?.[i] && resolveColor(colors[i]) || OPTION_HEX[i % OPTION_HEX.length];
      $[9] = colors;
      $[10] = t6;
    } else {
      t6 = $[10];
    }
    t5 = options.map(t6);
    $[6] = colors;
    $[7] = options;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[11] !== t4 || $[12] !== t5) {
    t6 = [{
      data: t4,
      backgroundColor: t5,
      borderWidth: 0
    }];
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t3 || $[15] !== t6) {
    t7 = {
      labels: t3,
      datasets: t6
    };
    $[14] = t3;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      responsive: false,
      maintainAspectRatio: false,
      animation: false,
      cutout: "62%",
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          enabled: false,
          external: donutTooltip
        }
      }
    };
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== size || $[19] !== t7) {
    t9 = <Doughnut width={size} height={size} data={t7} options={t8} />;
    $[18] = size;
    $[19] = t7;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const t10 = options[0]?.pct;
  let t11;
  if ($[21] !== t10) {
    t11 = <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm font-bold tabular-nums text-[var(--text-primary)]">{t10}%</span>;
    $[21] = t10;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t11 || $[24] !== t2 || $[25] !== t9) {
    t12 = <DeferredChart className="relative shrink-0" style={t2}>{t9}{t11}</DeferredChart>;
    $[23] = t11;
    $[24] = t2;
    $[25] = t9;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  return t12;
}

// Survival curve: share of runs still alive at each floor. Single gold
// series (no legend needed), soft area fill, hover crosshair via tooltip.
function _temp5(o_0) {
  return o_0.pct;
}
function _temp4(o) {
  return o.label;
}
export function SurvivalLine(t0) {
  const $ = _c(31);
  const {
    data,
    aliveLabel,
    floorLabel
  } = t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      display: false
    };
    t2 = {
      display: false
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      grid: t1,
      border: t2,
      ticks: {
        color: TEXT_MUTED,
        font: {
          size: 11
        },
        maxTicksLimit: 12
      }
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      display: false
    };
    t5 = {
      color: "rgba(138, 138, 147, 0.15)"
    };
    $[3] = t4;
    $[4] = t5;
  } else {
    t4 = $[3];
    t5 = $[4];
  }
  let t6;
  let t7;
  let t8;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      x: t3,
      y: {
        beginAtZero: true,
        max: 100,
        border: t4,
        grid: t5,
        ticks: {
          color: TEXT_MUTED,
          font: {
            size: 11
          },
          callback: _temp6
        }
      }
    };
    t7 = {
      point: {
        radius: 0,
        hitRadius: 10,
        hoverRadius: 4
      }
    };
    t8 = {
      mode: "index",
      intersect: false
    };
    $[5] = t6;
    $[6] = t7;
    $[7] = t8;
  } else {
    t6 = $[5];
    t7 = $[6];
    t8 = $[7];
  }
  let t9;
  if ($[8] !== floorLabel) {
    t9 = items => `${floorLabel} ${items[0]?.label}`;
    $[8] = floorLabel;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  if ($[10] !== aliveLabel) {
    t10 = item => `${item.parsed.y}% ${aliveLabel}`;
    $[10] = aliveLabel;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] !== t10 || $[13] !== t9) {
    t11 = {
      ...TOOLTIP_BASE,
      callbacks: {
        title: t9,
        label: t10
      }
    };
    $[12] = t10;
    $[13] = t9;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      display: false
    };
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  let t13;
  if ($[16] !== t11) {
    t13 = {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      scales: t6,
      elements: t7,
      interaction: t8,
      plugins: {
        tooltip: t11,
        datalabels: t12
      }
    };
    $[16] = t11;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  const options = t13;
  let t14;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      height: 220
    };
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  let t15;
  if ($[19] !== data) {
    t15 = data.map(_temp7);
    $[19] = data;
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  let t16;
  if ($[21] !== data) {
    t16 = data.map(_temp8);
    $[21] = data;
    $[22] = t16;
  } else {
    t16 = $[22];
  }
  let t17;
  if ($[23] !== t16) {
    t17 = [{
      data: t16,
      borderColor: GOLD,
      backgroundColor: "rgba(212, 168, 67, 0.16)",
      fill: true,
      borderWidth: 2,
      tension: 0.25
    }];
    $[23] = t16;
    $[24] = t17;
  } else {
    t17 = $[24];
  }
  let t18;
  if ($[25] !== t15 || $[26] !== t17) {
    t18 = {
      labels: t15,
      datasets: t17
    };
    $[25] = t15;
    $[26] = t17;
    $[27] = t18;
  } else {
    t18 = $[27];
  }
  let t19;
  if ($[28] !== options || $[29] !== t18) {
    t19 = <DeferredChart style={t14}><Line data={t18} options={options} /></DeferredChart>;
    $[28] = options;
    $[29] = t18;
    $[30] = t19;
  } else {
    t19 = $[30];
  }
  return t19;
}
function _temp8(d_0) {
  return d_0.alive_pct;
}
function _temp7(d) {
  return d.floor;
}
function _temp6(v) {
  return `${v}%`;
}